/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// This file co-locates the `buttonVariants` cva() helper with the Button
// component per the shadcn/ui convention. Fast Refresh needs a file to
// export components only; splitting `buttonVariants` into a sibling file
// is a design-system refactor that is out of scope for the lint-cleanup
// pass. Falling back to a full page reload on edits here is acceptable —
// the file changes infrequently.
/* eslint-disable react-refresh/only-export-components */

import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xs text-xs leading-4 font-semibold uppercase tracking-button transition-all outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 [&_svg]:shrink-0 focus-visible:ring-3',
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-primary/80 focus-visible:border-ring focus-visible:ring-focus',
                primary:
                    'bg-primary text-primary-foreground hover:bg-primary/80 focus-visible:border-ring focus-visible:ring-focus',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:border-ring focus-visible:ring-focus',
                destructive:
                    'bg-destructive text-white hover:bg-destructive/90 focus-visible:border-destructive focus-visible:ring-destructive/30 dark:focus-visible:ring-destructive/40',
                outline:
                    'border border-input bg-background text-foreground hover:bg-secondary focus-visible:border-ring focus-visible:ring-focus',
                ghost: 'bg-transparent text-foreground hover:bg-secondary focus-visible:border-ring focus-visible:ring-focus',
                link: 'relative !h-auto !rounded-none !bg-transparent !px-0 !py-0 text-foreground no-underline [text-decoration-style:solid] [text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-thin underline-offset-[5px] hover:bg-transparent hover:text-foreground hover:underline hover:decoration-warning focus-visible:border-ring focus-visible:ring-focus disabled:text-muted-foreground disabled:no-underline',
            },
            size: {
                default: 'h-12 px-4 py-4',
                large: 'px-8 py-5',
                lg: 'h-12 px-6 py-4',
                sm: 'h-10 px-4 py-3',
                small: 'h-10 px-4 py-3',
                icon: 'size-12',
                'icon-sm': 'size-8',
                'icon-lg': 'size-10',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'default',
        },
    }
);

export type ButtonProps = ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
    };

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
}

export { buttonVariants };
